import {
  canonicalYouTubeUrl,
  createPlaylistEntry,
  extractYouTubePlaylistId,
  type PlaylistEntry,
} from "@protv-converter/core";

interface YouTubeThumbnail {
  url?: string;
}

interface YouTubePlaylistItem {
  snippet?: {
    title?: string;
    description?: string;
    resourceId?: { videoId?: string };
    thumbnails?: Record<string, YouTubeThumbnail | undefined>;
  };
  status?: { privacyStatus?: string };
}

interface YouTubePlaylistResponse {
  nextPageToken?: string;
  items?: YouTubePlaylistItem[];
  error?: { message?: string; errors?: Array<{ reason?: string }> };
}

export class YouTubeApiError extends Error {
  readonly code: "invalid-key" | "quota" | "not-found" | "forbidden" | "network" | "unknown";

  constructor(code: YouTubeApiError["code"], message: string) {
    super(message);
    this.name = "YouTubeApiError";
    this.code = code;
  }
}

function classifyError(status: number, body: YouTubePlaylistResponse): YouTubeApiError {
  const reason = body.error?.errors?.[0]?.reason ?? "";
  const message = body.error?.message ?? "YouTube could not complete this request.";

  if (/keyInvalid|API_KEY_INVALID|accessNotConfigured/i.test(reason + message)) return new YouTubeApiError("invalid-key", "The YouTube API key is invalid or the Data API is not enabled.");
  if (/quota/i.test(reason + message)) return new YouTubeApiError("quota", "This API key has reached its YouTube quota.");
  if (status === 404 || /playlistNotFound/i.test(reason)) return new YouTubeApiError("not-found", "That YouTube playlist could not be found.");
  if (status === 403) return new YouTubeApiError("forbidden", "The playlist is private, inaccessible, or blocked for this API key.");
  return new YouTubeApiError("unknown", message);
}

async function fetchJson(url: URL, signal?: AbortSignal): Promise<YouTubePlaylistResponse> {
  let response: Response;
  try {
    response = await fetch(url, { signal });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error;
    throw new YouTubeApiError("network", "The YouTube request could not reach Google. Check the connection and key restrictions.");
  }

  const body = (await response.json()) as YouTubePlaylistResponse;
  if (!response.ok) throw classifyError(response.status, body);
  return body;
}

export async function validateYouTubeApiKey(apiKey: string): Promise<void> {
  const url = new URL("https://www.googleapis.com/youtube/v3/videos");
  url.search = new URLSearchParams({ part: "id", id: "dQw4w9WgXcQ", key: apiKey }).toString();
  await fetchJson(url);
}

function bestThumbnail(item: YouTubePlaylistItem): string {
  const thumbnails = item.snippet?.thumbnails;
  return thumbnails?.maxres?.url ?? thumbnails?.high?.url ?? thumbnails?.medium?.url ?? thumbnails?.default?.url ?? "";
}

export async function loadYouTubePlaylist(
  input: string,
  apiKey: string,
  onPage?: (loaded: number) => void,
  signal?: AbortSignal,
): Promise<PlaylistEntry[]> {
  const playlistId = extractYouTubePlaylistId(input);
  if (!playlistId) throw new YouTubeApiError("not-found", "Enter a YouTube playlist URL or playlist ID.");
  if (!apiKey.trim()) throw new YouTubeApiError("invalid-key", "Add your YouTube Data API key before loading a playlist.");

  const entries: PlaylistEntry[] = [];
  let nextPageToken = "";

  do {
    const url = new URL("https://www.googleapis.com/youtube/v3/playlistItems");
    const params = new URLSearchParams({
      part: "snippet,status",
      playlistId,
      maxResults: "50",
      key: apiKey,
      fields: "nextPageToken,items(snippet(title,description,resourceId/videoId,thumbnails),status/privacyStatus)",
    });
    if (nextPageToken) params.set("pageToken", nextPageToken);
    url.search = params.toString();

    const page = await fetchJson(url, signal);
    for (const item of page.items ?? []) {
      const videoId = item.snippet?.resourceId?.videoId;
      const title = item.snippet?.title?.trim() ?? "";
      const privacy = item.status?.privacyStatus?.toLowerCase();
      if (!videoId || title === "Deleted video" || title === "Private video" || privacy === "private") continue;

      entries.push(
        createPlaylistEntry({
          mainUrl: canonicalYouTubeUrl(videoId),
          title: title || `YouTube video ${videoId}`,
          description: "",
          source: "youtube",
          youtubeVideoId: videoId,
          thumbnailUrl: bestThumbnail(item),
        }),
      );
    }

    onPage?.(entries.length);
    nextPageToken = page.nextPageToken ?? "";
  } while (nextPageToken);

  return entries;
}
